import type { GetApiV1ThreeLetterCodePartnerAdminsClientContracts200 } from '@fukuri-admins/apis/client/gen/model';
import { formatDate } from '@fukuri-admins/utils/common/date';
import { getContractPlanLink } from '~/utils/getLinks';
import type { ClientContractListPresentation } from '../../presentations';

type PresentationProps = React.ComponentProps<typeof ClientContractListPresentation>;

type Args = {
  /** API レスポンス全体 */
  data: GetApiV1ThreeLetterCodePartnerAdminsClientContracts200;
  /**
   * 検索条件適用中かどうか
   *
   * 検索結果 0 件と全件 0 件（Empty 状態）を区別するために使用する
   */
  isFiltered: boolean;
};

type IConvertToPresentationProps = (args: Args) => {
  clientContracts: PresentationProps['clientContracts'];
  /** データ 0 件かつ検索条件なしのとき true（Empty コンポーネントを表示する条件） */
  isEmpty: boolean;
};

/**
 * 企業契約期間一覧の API レスポンスを Presentation の Props 形式に変換する
 *
 * - 開始日・終了日は「YYYY/MM/DD」形式に変換する
 * - ended_on が null の場合は「-」（無期限契約）を表示する
 * - 全件 0 件かつ検索条件なし（isEmpty）のときだけ Container 層で Empty 画面に切り替える
 * - createdAt は UNIX 秒（created_at）を「YYYY/MM/DD HH:mm」形式に変換する
 * - ページネーション（pager）の組み立ては Container 層（generatePagerProps）が担う
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({ data, isFiltered }) => {
  const clientContracts = (data.client_contracts ?? []).map(contract => ({
    id: contract.id,
    clientId: contract.client.id,
    clientName: contract.client.name,
    contractPlanName: contract.contract_plan.name,
    contractPlanLink: getContractPlanLink(contract.contract_plan.id),
    startedOn: contract.started_on
      ? formatDate({ date: new Date(contract.started_on), formatType: 'yyyy/MM/dd' })
      : '-',
    endedOn: contract.ended_on
      ? formatDate({ date: new Date(contract.ended_on), formatType: 'yyyy/MM/dd' })
      : '-',
    createdAt: formatDate({
      date: new Date(contract.created_at * 1000),
      formatType: 'yyyy/MM/dd HH:mm',
    }),
  }));

  // 検索条件なし & 0 件 → Empty 画面
  const isEmpty = clientContracts.length === 0 && !isFiltered;

  return { clientContracts, isEmpty };
};
